import React, {useState} from "react";
import {Link} from "react-router-dom";
import DOMPurify from "dompurify";
import ArticleDTO from "../../../../Models/DTOs/ArticleDTO";
import {RoutePaths} from "../../../../Constants/RoutePaths";

interface ArticleFeedCardProps {
    article: ArticleDTO;
    variant?: "hero" | "card" | "compact";
    headingLevel?: 2 | 3;
    showStaffPick?: boolean;
}

// Headlines and summaries can contain formatting from the editor. Feed cards
// use plain text so a pasted heading or paragraph cannot change their layout.
const feedText = (value?: string | null): string => value
    ? DOMPurify.sanitize(value, {ALLOWED_TAGS: [], ALLOWED_ATTR: [], RETURN_DOM: true}).textContent?.trim() || ""
    : "";

const ArticleFeedCard: React.FC<ArticleFeedCardProps> = ({article, variant = "card", headingLevel, showStaffPick = false}) => {
    const [failedImage, setFailedImage] = useState<string | null>(null);
    const title = feedText(article.title) || "Untitled article";
    const summary = feedText(article.subTitle);
    const mobileSummary = feedText(article.mobileSubTitle) || summary;
    const category = article.categories?.find(item => item.title?.trim());
    const author = article.authors?.map(item => item.Name?.trim()).filter(Boolean).join(", ");
    const releaseDate = article.releaseDate?.trim();
    const utcReleaseDate = releaseDate && !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(releaseDate)
        ? `${releaseDate}Z` : releaseDate;
    const date = utcReleaseDate ? new Date(utcReleaseDate) : null;
    const validDate = date && !isNaN(date.getTime()) ? date : null;
    const Heading = (headingLevel || (variant === "hero" ? 2 : 3)) === 2 ? "h2" : "h3";
    const articlePath = RoutePaths.ReadArticle(article.id);
    const imageSource = article.header?.trim();

    return (
        <article className={`article-feed-card article-feed-card--${variant}`}>
            <Link className="article-feed-card__image" to={articlePath} aria-label={`Read: ${title}`}>
                {imageSource && failedImage !== imageSource ? (
                    <img
                        src={imageSource}
                        alt=""
                        loading={variant === "hero" ? "eager" : "lazy"}
                        decoding="async"
                        width={640}
                        height={360}
                        onError={() => setFailedImage(imageSource)}
                    />
                ) : (
                    <span className="article-feed-card__image-fallback" aria-hidden="true">Ickonic</span>
                )}
            </Link>
            <div className="article-feed-card__content">
                {(category || showStaffPick) && (
                    <div className="article-feed-card__kicker">
                        {category && <Link to={RoutePaths.ArticleCategory(category.id)}>{category.title}</Link>}
                        {showStaffPick && <span className="article-feed-card__pick">Staff pick</span>}
                    </div>
                )}
                <Heading className="article-feed-card__title"><Link to={articlePath}>{title}</Link></Heading>
                {(summary || mobileSummary) && (
                    <p className="article-feed-card__summary">
                        {summary && <span className="article-feed-card__summary-desktop">{summary}</span>}
                        {mobileSummary && <span className="article-feed-card__summary-mobile">{mobileSummary}</span>}
                    </p>
                )}
                {(author || validDate) && (
                    <div className="article-feed-card__meta">
                        {author && <span className="article-feed-card__author">By {author}</span>}
                        {validDate && <time dateTime={validDate.toISOString()}>{validDate.toLocaleDateString("en-GB", {
                            day: "numeric", month: "short", year: "numeric", timeZone: "UTC"
                        })}</time>}
                    </div>
                )}
            </div>
        </article>
    );
};

export default ArticleFeedCard;
